import React, { useState, useEffect } from 'react'
import Modals from '../../shared/Modals'
import { Label } from 'reactstrap'
import { formatCurrency } from '../../shared/utils'
import { InputNumber } from 'primereact/inputnumber'
import {
  shouldConfirmHighQuantity,
  openHighQuantityConfirmDialog,
} from '../highQuantityOrder'
import {
  getMaxOrderQuantityPerOrder,
  normalizeLineQty,
  openMaxOrderPerOrderDialog,
} from '../maxOrderPerOrder'
import {
  getMinOrderQuantity,
  openMinOrderPerOrderDialog,
} from '../minOrderPerOrder'

/**
 * @param {number|null|undefined} originalLineQuantity — `null`/`undefined`: new line (e.g. add from alternatives). A number: quantity on the line when the modal opened (edit from cart / search).
 */
const EditOrderItem = ({
  visible,
  setVisible,
  data,
  editItem,
  originalLineQuantity,
}) => {
  const [orderDetails, setOrderDetails] = useState({
    quantity: 0,
    subtotal: 0,
  })

  useEffect(() => {
    setOrderDetails({
      quantity: data?.quantity,
      subtotal: data?.subtotal,
    })
  }, [data])

  const updateSubtotal = (quantity) => {
    const newSubtotal = data.sellingPrice * quantity
    setOrderDetails({
      quantity: quantity,
      subtotal: newSubtotal,
    })
  }

  const commitSave = () => {
    const updatedData = {
      ...data,
      quantity: orderDetails.quantity,
      subtotal: orderDetails.subtotal,
    }
    editItem(updatedData)
  }

  const trySubmit = () => {
    const qty = normalizeLineQty(orderDetails.quantity)
    const minPerOrder = getMinOrderQuantity(data)
    if (minPerOrder != null && Number.isFinite(qty) && qty < minPerOrder) {
      openMinOrderPerOrderDialog({
        itemLabel:
          data.itemDescription || data.retailUpcCode || 'This product',
        requestedQty: qty,
        minQty: minPerOrder,
        onUseMin: () => {
          const sub = minPerOrder * data.sellingPrice
          const updated = {
            ...data,
            quantity: minPerOrder,
            tempQuantity: minPerOrder,
            subtotal: sub,
          }
          setOrderDetails({ quantity: minPerOrder, subtotal: sub })
          editItem(updated)
          setVisible(false)
        },
      })
      return
    }

    const maxPerOrder = getMaxOrderQuantityPerOrder(data)
    if (maxPerOrder != null && Number.isFinite(qty) && qty > maxPerOrder) {
      openMaxOrderPerOrderDialog({
        itemLabel:
          data.itemDescription || data.retailUpcCode || 'This product',
        requestedQty: qty,
        maxQty: maxPerOrder,
        onUseMax: () => {
          const sub = maxPerOrder * data.sellingPrice
          const updated = {
            ...data,
            quantity: maxPerOrder,
            tempQuantity: maxPerOrder,
            subtotal: sub,
          }
          setOrderDetails({ quantity: maxPerOrder, subtotal: sub })
          editItem(updated)
          setVisible(false)
        },
      })
      return
    }

    const needsConfirm = shouldConfirmHighQuantity({
      newQuantity: Number.isFinite(qty) ? qty : orderDetails.quantity,
      originalLineQuantity,
    })

    if (!needsConfirm) {
      commitSave()
      setVisible(false)
      return
    }

    openHighQuantityConfirmDialog({
      quantity: Number.isFinite(qty) ? qty : Number(orderDetails.quantity),
      onAccept: () => {
        commitSave()
        setVisible(false)
      },
    })
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      trySubmit()
    }
  }

  const maxPerOrderCap = getMaxOrderQuantityPerOrder(data)
  const minPerOrderQty = getMinOrderQuantity(data)

  return (
    <Modals
      btnName={'Save'}
      ModalHeader={'Edit Item Details'}
      visible={visible}
      setVisible={setVisible}
      wait
      disabled={
        orderDetails.subtotal === null ||
        orderDetails.subtotal === 0 ||
        orderDetails.subtotal === ''
          ? true
          : false
      }
      onButtonClick={trySubmit}
    >
        <div className='row'>
          <h5 className='col-sm'>Item</h5>
          <p className='col-sm'>{data?.itemCode}</p>
        </div>
        <div className='row'>
          <h5 className='col-sm'>UPC#</h5>
          <p className='col-sm'>{data?.retailUpcCode}</p>
        </div>
        <div className='row'>
          <h5 className='col-sm'>Product Description</h5>
          <p className='col-sm'>{data?.itemDescription}</p>
        </div>
        <div className='row'>
          <h5 className='col-sm'>Manufacturer</h5>
          <p className='col-sm'>{data?.manufacturer?.name}</p>
        </div>
        <div className='row'>
          <h5 className='col-sm'>DIN/NPN</h5>
          <p className='col-sm'>{data?.drugIdentificationNo}</p>
        </div>
        <div className='row'>
          <h5 className='col-sm'>Price</h5>
          <p className='col-sm'>{formatCurrency(data?.sellingPrice)}</p>
        </div>
        {minPerOrderQty != null && (
          <div className='row'>
            <h5 className='col-sm'>Min qty per order</h5>
            <p className='col-sm'>{minPerOrderQty}</p>
          </div>
        )}
        {maxPerOrderCap != null && (
          <div className='row'>
            <h5 className='col-sm'>Max qty per order</h5>
            <p className='col-sm'>{maxPerOrderCap}</p>
          </div>
        )}
        <div className='row'>
          <Label className='form-label col-sm' htmlFor='quantity'>
            Quantity
          </Label>

          <InputNumber
            inputId='quantity'
            value={orderDetails.quantity}
            onChange={(e) => updateSubtotal(e.value)}
            mode='decimal'
            placeholder='Enter Quantity'
            showButtons
            min={1}
            className=' col-sm mb-2'
            onKeyPress={handleKeyPress}
          />
        </div>
        <div className='row'>
          <h5 className='col-sm'>Subtotal</h5>
          <p className='col-sm'>
            {formatCurrency(
              orderDetails.subtotal === null || orderDetails.subtotal === 0
                ? data.subtotal
                : orderDetails.subtotal
            )}
          </p>
        </div>
    </Modals>
  )
}

export default EditOrderItem
